import { Box } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import type { ConceptSeverity, EntityType } from '@/services/api/schemas'
import { cn } from '@/utils/cn'
import { RowFindingBadge } from './RowFindingBadge'

export interface EntityListItemProps {
  entity: EntityType
  isSelected: boolean
  onSelect: (entity: EntityType) => void
  /** Worst validation-finding severity on this entity, after a Validate run. */
  severity?: ConceptSeverity
}

/** A single entity row in the entities list column. */
export function EntityListItem({ entity, isSelected, onSelect, severity }: EntityListItemProps) {
  const { t } = useTranslation()
  return (
    <div
      onClick={() => onSelect(entity)}
      className={cn(
        'flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all border',
        isSelected ? 'bg-muted border-info-border' : 'bg-card/50 border-border hover:border-border'
      )}
    >
      <Box className="h-4 w-4 text-info-foreground shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-foreground truncate">{entity.label}</div>
        {entity.description && (
          <div className="text-xs text-muted-foreground truncate">{entity.description}</div>
        )}
      </div>
      <RowFindingBadge severity={severity} />
      <Badge variant="outline" className="text-xs bg-accent/50 border-border shrink-0">
        {t('schemaAgent.editor.propertyCount', { count: entity.properties.length })}
      </Badge>
    </div>
  )
}
